// =============================================================================
// As análises do Painel Financeiro de Ferramentas (067, 069)
//
// Tudo aqui é conta sobre a lista de ferramentas que a API já devolve — a
// mesma que a RLS recorta (o gestor vê só os CCs dele). Em centavos e BigInt,
// nunca float, como o resto do sistema.
//
// Duas regras de leitura valem para o painel inteiro:
//   · cancelada não conta;
//   · sem ciclo (para conferir), vale o mensal da planilha, e o painel diz
//     quantas estão assim — estimativa marcada, nunca escondida.
// =============================================================================

import type { CobradoArea, CobrancaFerramentaMes, CobrancaMes, Ferramenta, GrupoCobranca } from "./api.ts";
import type { Cotacao as CotacaoApi } from "./ferramentaCiclo.ts";

export type Familia = "ia" | "ensino" | "marketing" | "design" | "infraestrutura" | "comunicacao" | "gestao" | "outros";

export const FAMILIAS: { valor: Familia; rotulo: string }[] = [
  { valor: "ia", rotulo: "Inteligência artificial" },
  { valor: "ensino", rotulo: "Ensino e alunos" },
  { valor: "marketing", rotulo: "Marketing e vendas" },
  { valor: "design", rotulo: "Design e vídeo" },
  { valor: "infraestrutura", rotulo: "Infraestrutura e dados" },
  { valor: "comunicacao", rotulo: "Comunicação" },
  { valor: "gestao", rotulo: "Gestão e administrativo" },
  { valor: "outros", rotulo: "Outros" },
];
export const ROTULO_FAMILIA = Object.fromEntries(FAMILIAS.map((f) => [f.valor, f.rotulo])) as Record<Familia, string>;

const big = (v: string | null | undefined): bigint => (v ? BigInt(v) : 0n);

export const vivas = (fs: Ferramenta[]): Ferramenta[] => fs.filter((f) => f.status !== "cancelada");

/** O custo no ano: com ciclo, o da view; sem ciclo, o mensal da planilha × 12. */
export function custoAnual(f: Ferramenta): bigint | null {
  if (f.custo_anual_centavos) return BigInt(f.custo_anual_centavos);
  if (!f.ciclo && f.custo_mensal_centavos) return BigInt(f.custo_mensal_centavos) * 12n;
  return null;
}

/** Percentual de `parte` sobre `total`, com uma casa. */
export function pct(parte: bigint, total: bigint): number {
  return total > 0n ? Number((parte * 1000n) / total) / 10 : 0;
}

/** Dias de `de` até `ate`, as duas "AAAA-MM-DD". Em UTC dos dois lados: sem fuso no meio. */
function dias(de: string, ate: string): number {
  const d = (s: string) => { const [a, m, dd] = s.split("-").map(Number); return Date.UTC(a!, m! - 1, dd!); };
  return Math.round((d(ate) - d(de)) / 86_400_000);
}

/** "2026-10" + n meses. */
export function somarMeses(mes: string, n: number): string {
  const [a, m] = mes.split("-").map(Number);
  const t = (a! * 12 + (m! - 1)) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
}

export type Resumo = {
  ativas: number;
  mensal: bigint;
  anual: bigint;
  mensalUsd: bigint;
  semValor: number;
  paraConferir: number;
  renovacoes60: { n: number; valor: bigint };
};

export function resumir(fs: Ferramenta[], hoje: string): Resumo {
  const v = vivas(fs);
  const r: Resumo = { ativas: v.length, mensal: 0n, anual: 0n, mensalUsd: 0n, semValor: 0, paraConferir: 0, renovacoes60: { n: 0, valor: 0n } };
  for (const f of v) {
    const m = big(f.custo_mensal_centavos);
    r.mensal += m;
    r.anual += custoAnual(f) ?? 0n;
    if (f.moeda === "USD") r.mensalUsd += m;
    if (!f.custo_mensal_centavos && f.ciclo !== "pontual") r.semValor++;
    if (!f.ciclo) r.paraConferir++;
    if (f.proxima_renovacao) {
      const d = dias(hoje, f.proxima_renovacao);
      if (d >= 0 && d <= 60) {
        r.renovacoes60.n++;
        r.renovacoes60.valor += big(f.valor_ciclo_reais_centavos) || (custoAnual(f) ?? 0n);
      }
    }
  }
  return r;
}

export type Grupo = { chave: string; rotulo: string; mensal: bigint; anual: bigint; n: number; ferramentas: Ferramenta[] };

/** Soma por uma chave (área, conta, família, cartão), do maior custo mensal para o menor. */
export function agrupar(fs: Ferramenta[], chave: (f: Ferramenta) => string, rotulo: (f: Ferramenta) => string): Grupo[] {
  const m = new Map<string, Grupo>();
  for (const f of vivas(fs)) {
    const k = chave(f);
    const g = m.get(k) ?? { chave: k, rotulo: rotulo(f), mensal: 0n, anual: 0n, n: 0, ferramentas: [] };
    g.mensal += big(f.custo_mensal_centavos);
    g.anual += custoAnual(f) ?? 0n;
    g.n++;
    g.ferramentas.push(f);
    m.set(k, g);
  }
  return [...m.values()].sort((a, b) => (b.mensal > a.mensal ? 1 : b.mensal < a.mensal ? -1 : a.rotulo.localeCompare(b.rotulo)));
}

export const porArea = (fs: Ferramenta[]) => agrupar(fs, (f) => f.centro_custo_id ?? "",
  (f) => (f.centro_custo_id ? `${f.centro_custo_codigo} · ${f.centro_custo_nome}` : "Sem centro de custo"));
export const porConta = (fs: Ferramenta[]) => agrupar(fs, (f) => f.categoria_id ?? "",
  (f) => (f.categoria_id ? `${f.categoria_codigo ? `${f.categoria_codigo} ` : ""}${f.categoria_nome ?? ""}` : "Sem conta"));
export const porFamilia = (fs: Ferramenta[]) => agrupar(fs, (f) => f.familia ?? "outros", (f) => ROTULO_FAMILIA[f.familia ?? "outros"]);
export const porCartao = (fs: Ferramenta[]) => agrupar(fs.filter((f) => f.forma_pagamento === "cartao"),
  (f) => f.cartao_final ?? "", (f) => (f.cartao_final ? `Final ${f.cartao_final}` : "Cartão sem final informado"));

/** As mais caras, e quanto do custo elas somam. */
export function concentracao(fs: Ferramenta[], top = 10): { lista: Ferramenta[]; pctTop: number } {
  const v = vivas(fs).filter((f) => f.custo_mensal_centavos);
  const ordenadas = v.sort((a, b) => { const x = big(a.custo_mensal_centavos), y = big(b.custo_mensal_centavos); return y > x ? 1 : y < x ? -1 : 0; });
  const lista = ordenadas.slice(0, top);
  const total = ordenadas.reduce((s, f) => s + big(f.custo_mensal_centavos), 0n);
  return { lista, pctTop: pct(lista.reduce((s, f) => s + big(f.custo_mensal_centavos), 0n), total) };
}

export type MesDesembolso = { mes: string; recorrente: bigint; renovacoes: bigint };
export type Desembolso = {
  meses: MesDesembolso[];
  /** Anuais sem data de renovação: cobram em algum mês do ano, não se sabe qual. */
  semData: { n: number; valor: bigint };
  /** Contadas pelo mensal equivalente: sem ciclo, ou trimestral/semestral sem data. */
  aproximadas: number;
};

/**
 * O que sai do caixa nos próximos 12 meses. Mensal e por uso, todo mês; o
 * ciclo mais longo com data, no mês em que renova (e a cada ciclo depois);
 * sem data, o trimestral e o semestral entram pelo mensal equivalente e a
 * anual fica à parte. Pontual não entra: é compra feita.
 *
 * Com término previsto (079), a ferramenta não entra nos meses depois dele:
 * a Bubble que acaba em janeiro não pesa de fevereiro em diante.
 */
export function desembolso12(fs: Ferramenta[], hoje: string): Desembolso {
  const inicio = hoje.slice(0, 7);
  const meses: MesDesembolso[] = Array.from({ length: 12 }, (_, i) => ({ mes: somarMeses(inicio, i), recorrente: 0n, renovacoes: 0n }));
  const fim = meses[11]!.mes;
  const r: Desembolso = { meses, semData: { n: 0, valor: 0n }, aproximadas: 0 };
  const passo: Partial<Record<string, number>> = { trimestral: 3, semestral: 6, anual: 12 };
  for (const f of vivas(fs)) {
    const mensal = big(f.custo_mensal_centavos);
    const ate = f.teste_ate ? f.teste_ate.slice(0, 7) : null;
    const ativos = ate ? meses.filter((m) => m.mes <= ate) : meses;
    if (!f.ciclo) {
      if (mensal) { for (const m of ativos) m.recorrente += mensal; r.aproximadas++; }
      continue;
    }
    if (f.ciclo === "pontual") continue;
    if (f.ciclo === "mensal" || f.ciclo === "por_uso") {
      for (const m of ativos) m.recorrente += mensal;
      continue;
    }
    const p = passo[f.ciclo]!;
    const valor = big(f.valor_ciclo_reais_centavos);
    if (!f.proxima_renovacao) {
      if (f.ciclo === "anual") { if (!ate || ate >= inicio) { r.semData.n++; r.semData.valor += valor; } }
      else { for (const m of ativos) m.recorrente += mensal; r.aproximadas++; }
      continue;
    }
    let mes = f.proxima_renovacao.slice(0, 7);
    while (mes < inicio) mes = somarMeses(mes, p);
    // Renovação depois do término não acontece.
    for (; mes <= fim && (!ate || mes <= ate); mes = somarMeses(mes, p)) {
      const alvo = meses.find((x) => x.mes === mes);
      if (alvo) alvo.renovacoes += valor;
    }
  }
  return r;
}

export type LinhaOrcamento = { centro_custo_id: string; categoria_id: string; orcado_centavos: string; realizado_centavos: string };
export type FerramentaOrcamento = {
  chave: string;
  area: string;
  conta: string;
  n: number;
  /** O custo projetado das ferramentas no ano. */
  anual: bigint;
  orcado: bigint;
  realizado: bigint;
};

/**
 * Por área e conta, o custo projetado das ferramentas contra o orçado do
 * exercício na mesma coordenada (CC + conta) e o realizado até agora. Fica de
 * fora, e é contada, a ferramenta sem CC ou sem conta.
 */
export function contraOrcamento(fs: Ferramenta[], linhas: LinhaOrcamento[]): { linhas: FerramentaOrcamento[]; semCoordenada: number } {
  const orc = new Map(linhas.map((l) => [`${l.centro_custo_id}|${l.categoria_id}`, l]));
  const m = new Map<string, FerramentaOrcamento>();
  let semCoordenada = 0;
  for (const f of vivas(fs)) {
    if (!f.centro_custo_id || !f.categoria_id) { semCoordenada++; continue; }
    const k = `${f.centro_custo_id}|${f.categoria_id}`;
    const o = orc.get(k);
    const g = m.get(k) ?? {
      chave: k,
      area: `${f.centro_custo_codigo} · ${f.centro_custo_nome}`,
      conta: `${f.categoria_codigo ? `${f.categoria_codigo} ` : ""}${f.categoria_nome ?? ""}`,
      n: 0, anual: 0n, orcado: big(o?.orcado_centavos), realizado: big(o?.realizado_centavos),
    };
    g.n++;
    g.anual += custoAnual(f) ?? 0n;
    m.set(k, g);
  }
  return {
    linhas: [...m.values()].sort((a, b) => a.area.localeCompare(b.area, undefined, { numeric: true }) || (b.anual > a.anual ? 1 : -1)),
    semCoordenada,
  };
}

/** Quantas pessoas: o número, ou os nomes da lista ("Ana, Bruno e Carla"). */
export function contarUsuarios(f: Pick<Ferramenta, "qtd_usuarios" | "usuarios_ativos">): number | null {
  if (f.qtd_usuarios && f.qtd_usuarios > 0) return f.qtd_usuarios;
  const t = f.usuarios_ativos?.trim();
  if (!t) return null;
  const nomes = t.split(/[,;/\n]|\se\s/i).map((x) => x.trim()).filter((x) => x && !/^\d+$/.test(x));
  return nomes.length || null;
}

const REVISAR = /reduz|cancel|avaliar|rever|revis|substitu|downgrade|desativ|encerr/i;

export type Governanca = {
  paraConferir: Ferramenta[];
  semResponsavel: Ferramenta[];
  semCentro: Ferramenta[];
  semConta: Ferramenta[];
  /** A "Obs / Tendência" da planilha fala em reduzir, cancelar ou rever. */
  revisar: Ferramenta[];
  /** Famílias com mais de uma ferramenta em mais de uma área: onde olhar para consolidar. */
  consolidar: { familia: Familia; rotulo: string; n: number; areas: number; mensal: bigint }[];
  /** As de maior custo por pessoa, onde há quem use informado. */
  porUsuario: { f: Ferramenta; usuarios: number; porUsuario: bigint }[];
};

export function governanca(fs: Ferramenta[]): Governanca {
  const v = vivas(fs);
  const consolidar = porFamilia(fs)
    .map((g) => ({
      familia: g.chave as Familia, rotulo: g.rotulo, n: g.n, mensal: g.mensal,
      areas: new Set(g.ferramentas.map((f) => f.centro_custo_id ?? "")).size,
    }))
    .filter((g) => g.familia !== "outros" && g.n > 1 && g.areas > 1);
  const porUsuario = v
    .map((f) => ({ f, usuarios: contarUsuarios(f) ?? 0 }))
    .filter((x) => x.usuarios > 0 && x.f.custo_mensal_centavos)
    .map((x) => ({ ...x, porUsuario: big(x.f.custo_mensal_centavos) / BigInt(x.usuarios) }))
    .sort((a, b) => (b.porUsuario > a.porUsuario ? 1 : b.porUsuario < a.porUsuario ? -1 : 0));
  return {
    paraConferir: v.filter((f) => !f.ciclo),
    semResponsavel: v.filter((f) => !f.responsavel?.trim()),
    semCentro: v.filter((f) => !f.centro_custo_id),
    semConta: v.filter((f) => !f.categoria_id),
    revisar: v.filter((f) => REVISAR.test(f.tendencia ?? "")),
    consolidar,
    porUsuario,
  };
}

// ── As cobranças reconhecidas (070) ────────────────────────────────────────


/** O último mês fechado: o anterior ao de hoje, "AAAA-MM". */
export const mesFechado = (hoje: string): string => somarMeses(hoje.slice(0, 7), -1);

/** O cobrado de um mês: a cobrança mais o IOF que veio com o nome dela. */
const cobrado = (m: { real_centavos: string; iof_centavos: string }) => big(m.real_centavos) + big(m.iof_centavos);

/** Os fornecedores que têm alguma das ferramentas: o recorte por área do painel. */
export function chavesDe(grupos: GrupoCobranca[], fs: Ferramenta[]): Set<string> {
  const ids = new Set(fs.map((f) => f.id));
  return new Set(grupos.filter((g) => g.ferramentas.some((id) => ids.has(id))).map((g) => g.chave));
}

/** O total cobrado por mês, dos últimos `n` meses fechados (do mais antigo ao mais recente). */
export function cobradoPorMes(meses: CobrancaMes[], ate: string, n = 12, chaves?: Set<string>): { mes: string; cobrado: bigint }[] {
  const lista = Array.from({ length: n }, (_, i) => ({ mes: somarMeses(ate, i - n + 1), cobrado: 0n }));
  const idx = new Map(lista.map((x, i) => [x.mes, i]));
  for (const m of meses) {
    if (chaves && !chaves.has(m.chave)) continue;
    const i = idx.get(m.mes);
    if (i !== undefined) lista[i]!.cobrado += cobrado(m);
  }
  return lista;
}

export type LinhaFornecedor = {
  chave: string;
  /** Ferramentas ativas no grupo; 0 é fornecedor sem ferramenta (ou só com canceladas). */
  ativas: number;
  canceladas: number;
  esperado: bigint;
  /** O cobrado no mês fechado e a média dos 3 meses fechados. */
  ultimo: bigint;
  media3: bigint;
  /** Variação do último sobre o estimado, em %; nula sem estimado. */
  variacao: number | null;
  todasMensais: boolean;
};

/** Por fornecedor: o estimado do grupo contra o cobrado. Do maior cobrado para o menor. */
export function porFornecedor(grupos: GrupoCobranca[], meses: CobrancaMes[], ate: string, chaves?: Set<string>): LinhaFornecedor[] {
  const g = new Map(grupos.map((x) => [x.chave, x]));
  const tres = new Set([ate, somarMeses(ate, -1), somarMeses(ate, -2)]);
  const linhas = new Map<string, LinhaFornecedor>();
  for (const m of meses) {
    if (m.chave === "iof" || (chaves && !chaves.has(m.chave))) continue;
    const grupo = g.get(m.chave);
    const l = linhas.get(m.chave) ?? {
      chave: m.chave, ativas: grupo?.ativas ?? 0, canceladas: grupo?.canceladas ?? 0,
      esperado: big(grupo?.esperado_mensal_centavos), ultimo: 0n, media3: 0n, variacao: null, todasMensais: !!grupo?.todas_mensais,
    };
    if (m.mes === ate) l.ultimo += cobrado(m);
    if (tres.has(m.mes)) l.media3 += cobrado(m);
    linhas.set(m.chave, l);
  }
  return [...linhas.values()]
    .map((l) => ({ ...l, media3: l.media3 / 3n, variacao: l.esperado > 0n && l.todasMensais ? Number(((l.ultimo - l.esperado) * 1000n) / l.esperado) / 10 : null }))
    .filter((l) => l.ultimo > 0n || l.media3 > 0n)
    .sort((a, b) => (b.ultimo > a.ultimo ? 1 : b.ultimo < a.ultimo ? -1 : a.chave.localeCompare(b.chave)));
}

/**
 * O que o cartão cobrou, em média, por dólar: nas ferramentas em dólar
 * cobradas todo mês e reconhecidas uma a uma, o cobrado (com IOF) ÷ o US$ do
 * ciclo, nos 3 meses fechados. É a taxa efetiva — PTAX, IOF e spread juntos.
 */
export function taxaEfetivaDolar(fs: Ferramenta[], porFerr: CobrancaFerramentaMes[], ate: string, cotacao: CotacaoApi):
  { amostras: number; taxa: number; acrescimo: number | null } | null {
  const tres = new Set([ate, somarMeses(ate, -1), somarMeses(ate, -2)]);
  const usd = new Map(vivas(fs).filter((f) => f.moeda === "USD" && f.ciclo === "mensal" && big(f.valor_ciclo_centavos) > 0n)
    .map((f) => [f.id, big(f.valor_ciclo_centavos)]));
  const taxas: number[] = [];
  for (const c of porFerr) {
    const dolar = usd.get(c.ferramenta_id);
    if (!dolar || !tres.has(c.mes) || c.cobrancas !== 1) continue;
    taxas.push(Number(cobrado(c)) / Number(dolar));
  }
  if (taxas.length === 0) return null;
  taxas.sort((a, b) => a - b);
  const meio = Math.floor(taxas.length / 2);
  const taxa = taxas.length % 2 ? taxas[meio]! : (taxas[meio - 1]! + taxas[meio]!) / 2;
  const ptax = cotacao?.venda ? Number(cotacao.venda) : null;
  return { amostras: taxas.length, taxa, acrescimo: ptax ? taxa / ptax - 1 : null };
}

// ── O panorama por responsável (072) ───────────────────────────────────────

/** "Ana / Bruno e Carla" → ["Ana", "Bruno", "Carla"]: os separadores da planilha. */
export function separarNomes(texto: string | null | undefined): string[] {
  return (texto ?? "").split(/[,;/\n]|\se\s/i).map((x) => x.trim().replace(/\s+/g, " ")).filter(Boolean);
}

/** A mesma pessoa escrita de jeitos diferentes: sem acento, sem caixa. */
const chaveNome = (nome: string) => nome.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

export type LinhaResponsavel = {
  chave: string;
  nome: string;
  n: number;
  mensal: bigint;
  anual: bigint;
  /** Quantas das ferramentas dela têm mais de um responsável. */
  compartilhadas: number;
  ferramentas: Ferramenta[];
};

/**
 * Cada pessoa responsável, as ferramentas pelas quais responde e o custo delas.
 * A ferramenta com mais de um responsável entra em cada um com o custo inteiro:
 * é o que cada um responde, não uma divisão. Por isso a soma das linhas passa
 * do total, e a linha diz quantas são compartilhadas.
 */
export function porResponsavel(fs: Ferramenta[]): LinhaResponsavel[] {
  const m = new Map<string, LinhaResponsavel>();
  for (const f of vivas(fs)) {
    const nomes = separarNomes(f.responsavel);
    const unicos = [...new Map(nomes.map((n) => [chaveNome(n), n])).entries()];
    for (const [k, nome] of unicos) {
      const l = m.get(k) ?? { chave: k, nome, n: 0, mensal: 0n, anual: 0n, compartilhadas: 0, ferramentas: [] };
      l.n++;
      l.mensal += big(f.custo_mensal_centavos);
      l.anual += custoAnual(f) ?? 0n;
      if (unicos.length > 1) l.compartilhadas++;
      l.ferramentas.push(f);
      m.set(k, l);
    }
  }
  return [...m.values()].sort((a, b) => (b.mensal > a.mensal ? 1 : b.mensal < a.mensal ? -1 : a.nome.localeCompare(b.nome)));
}

// ── O cobrado da área (072, 073) ───────────────────────────────────────────

const valorCobrado = (l: CobradoArea) => big(l.cobrado_centavos) + big(l.iof_centavos);

/**
 * O cobrado (cobrança mais IOF) lançado na área `cc`, por mês, nos últimos `n`
 * meses fechados: o de cada ferramenta e o do grupo do fornecedor (073).
 */
export function cobradoAreaPorMes(linhas: CobradoArea[], cc: string, ate: string, n = 12): { mes: string; cobrado: bigint }[] {
  const lista = Array.from({ length: n }, (_, i) => ({ mes: somarMeses(ate, i - n + 1), cobrado: 0n }));
  const idx = new Map(lista.map((x, i) => [x.mes, i]));
  for (const l of linhas) {
    if (l.centro_custo_id !== cc) continue;
    const i = idx.get(l.mes);
    if (i !== undefined) lista[i]!.cobrado += valorCobrado(l);
  }
  return lista;
}

export type CobradoFerramenta = {
  /** O cobrado reconhecido como desta ferramenta. */
  proprio: bigint;
  /** O cobrado do grupo do fornecedor que ela divide com outras da área, se houve. */
  grupo: { chave: string; total: bigint; ferramentas: number } | null;
};

/** Num mês e numa área, o cobrado de cada ferramenta: o próprio e o do grupo dela. */
export function cobradoPorFerramenta(linhas: CobradoArea[], cc: string, mes: string): Map<string, CobradoFerramenta> {
  const m = new Map<string, CobradoFerramenta>();
  const de = (id: string) => m.get(id) ?? (m.set(id, { proprio: 0n, grupo: null }), m.get(id)!);
  for (const l of linhas) {
    if (l.mes !== mes || l.centro_custo_id !== cc) continue;
    if (l.ferramenta_id) { de(l.ferramenta_id).proprio += valorCobrado(l); continue; }
    for (const id of l.ferramentas) {
      const c = de(id);
      c.grupo = { chave: l.chave, total: (c.grupo?.total ?? 0n) + valorCobrado(l), ferramentas: l.ferramentas.length };
    }
  }
  return m;
}

/**
 * A marca da lista do gestor, só para quem cobra todo mês: nada lançado na
 * área no mês fechado, ou acima do estimado pelo mesmo limite do alerta de
 * valor da 070 (30%). Ficam sem marca a recém-contratada ("nova"), que ainda
 * não foi cobrada; anual, trimestral e pontual, que não cobram todo mês; por
 * uso, que varia por natureza; e a cobrada no grupo do fornecedor (073), que
 * não se separa por ferramenta.
 */
export function marcaCobranca(f: Ferramenta, c: CobradoFerramenta | undefined): "sem_lancamento" | "acima" | null {
  if (f.status === "cancelada" || f.status === "nova" || f.ciclo !== "mensal") return null;
  const estimado = big(f.custo_mensal_centavos);
  if (estimado <= 0n) return null;
  const proprio = c?.proprio ?? 0n;
  if (!proprio) return c?.grupo ? null : "sem_lancamento";
  return proprio * 10n > estimado * 13n ? "acima" : null;
}

